import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { testimonialsData } from '../data/mock';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const next = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev === testimonialsData.testimonials.length - 1 ? 0 : prev + 1));
  };

  const prev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev === 0 ? testimonialsData.testimonials.length - 1 : prev - 1));
  };

  const current = testimonialsData.testimonials[currentIndex];

  return (
    <section id="temoignages" className="py-24 bg-warmgray" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">
            Temoignages
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-navy tracking-tight">
            {testimonialsData.title}
          </h2>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-black/5 overflow-hidden">
          <div className="grid md:grid-cols-5 gap-0">
            <div className="md:col-span-2 relative h-72 md:h-auto min-h-[320px] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={current.image}
                  alt={current.name}
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
            </div>

            <div className="md:col-span-3 p-8 md:p-12 flex flex-col justify-center">
              <Quote className="w-12 h-12 text-forest/20 mb-6" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: direction * 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -30 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="text-xl md:text-2xl text-gray-700 leading-relaxed italic font-serif mb-8">
                    "{current.content}"
                  </p>

                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-forest/30">
                      <img src={current.image} alt={current.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h4 className="text-lg font-serif font-bold text-navy">{current.name}</h4>
                      <p className="text-sm text-gray-500 font-sans">{current.role}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center mt-10 space-x-4">
                <button
                  onClick={prev}
                  data-testid="testimonial-prev"
                  className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-navy hover:text-white hover:border-navy transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex space-x-2">
                  {testimonialsData.testimonials.map((t, i) => (
                    <button
                      key={t.name || `testimonial-${i}`}
                      onClick={() => {
                        setDirection(i > currentIndex ? 1 : -1);
                        setCurrentIndex(i);
                      }}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === currentIndex ? 'w-8 bg-forest' : 'w-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={next}
                  data-testid="testimonial-next"
                  className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-navy hover:text-white hover:border-navy transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
